'use client';

import { ChangeEvent, FormEvent, ReactNode, memo } from 'react';
import clsx from 'clsx';
import { DataTestConfig } from '@headless-workspace/config';
import { GlowIcon, GlowTextField, Icons, PropsWithAriaLabel, PropsWithClassName } from '@headless-workspace/glow-ds';
import { ComponentMemo } from '@headless-workspace/typings';
import { StringHelpers } from '@headless-workspace/utils';

type SearchBarProps = PropsWithClassName &
    PropsWithAriaLabel & {
        placeholder: string;
        onFocus: () => void;
        onSearch: (value: string) => void;
        clearButton: ReactNode | ReactNode[];
        searchInput: string;
        onSearchInputChange: (input: string) => void;
        searchLabel: string;
        isMobile: boolean;
    };

export const UISearchBar: ComponentMemo<SearchBarProps> = memo(
    ({
        className,
        placeholder,
        onFocus,
        onSearch,
        ariaLabel,
        clearButton,
        searchInput,
        onSearchInputChange,
        searchLabel,
        isMobile,
    }) => {
        'use memo';

        const onSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            onSearch(searchInput);
        };

        const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
            onSearchInputChange(event.target.value);
        };

        const inputId = StringHelpers.addPrefix('search-input', 'mobile-', isMobile);

        return (
            <form
                className={clsx(
                    'flex flex-row items-center justify-between',
                    'bg-surface-neutral rounded-infinity w-full h-searchBar py-0.5 px-0.75 gap-0.75',
                    className,
                )}
                role={'search'}
                onSubmit={onSearchSubmit}
            >
                <div className={'flex flex-row items-center w-full'}>
                    <label className={'hidden'} htmlFor={inputId}>
                        {searchLabel}
                    </label>
                    <GlowIcon Icon={Icons.Search} type={'large'} className={'text-text-primary'} />

                    <GlowTextField
                        className={clsx(
                            'mx-0.25 flex-grow-1 h-auto bg-surface-neutral',
                            'focus:outline-none scale-0.875 origin-left -mr-2 text-text-primary placeholder:text-text-tertiary',
                            'overflow-ellipsis',
                            'py-1',
                        )}
                        id={inputId}
                        type={'search'}
                        name={'search'}
                        inputMode={'search'}
                        enterKeyHint={'search'}
                        placeholder={placeholder}
                        value={searchInput}
                        onChange={onSearchChange}
                        autoComplete={'off'}
                        aria-label={ariaLabel}
                        aria-autocomplete={'none'}
                        dataTestId={DataTestConfig.testIds.searchBar}
                        onFocus={onFocus}
                    />
                </div>
                {clearButton}
            </form>
        );
    },
);

UISearchBar.displayName = 'UISearchBar';
